import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import {
  fetchQuestionPaper,
  saveQuestionPaper,
  uploadQuestionPaperFile,
  deleteQuestionPaperFile,
} from './api/testApi'

const SET_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
]

const ANSWER_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'X']

const TIPS = [
  'Use this option to upload Question Paper and set parameters for Online Tests',
  'Only PDF files can be uploaded as Question Paper.',
  'Students can log-in to student portal to take the test',
  'Test can only be taken during time specified as Start Time and End Time of the test while creating the test',
  'Specify marks to be given for correct answers and marks to be deducted for wrong answers (if applicable)',
  'Specify No. of Questions and time duration (in minutes)',
  'Click on [Refresh] button to generate options (Answer Key) for specified No. of Questions',
  'Define answer key for each question. Select correct answer (A to E) and X for Wrong Questions. If you select X as answer key, all answers are treated as right answer.',
  'Use [GET] button to create text string of answer key and use [SET] button to mark answerkey from text string.',
]

export default function QuestionPaper() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const testId = searchParams.get('id')

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasFile, setHasFile] = useState(false)
  const [fileName, setFileName] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)

  const [totalTime, setTotalTime] = useState('60')
  const [correctMarks, setCorrectMarks] = useState('1')
  const [wrongMarks, setWrongMarks] = useState('0')
  const [numberOfSets, setNumberOfSets] = useState(1)
  const [numberOfQuestions, setNumberOfQuestions] = useState('')
  const [questionStartsFrom, setQuestionStartsFrom] = useState('1')
  const [answerKeyText, setAnswerKeyText] = useState('')

  // Multi-set answer matrix: array of set arrays [ [{questionNo, answer}, ...], ... ]
  const [setMatrices, setSetMatrices] = useState([])

  const loadData = useCallback(async () => {
    if (!testId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const data = await fetchQuestionPaper(Number(testId))
      setHasFile(Boolean(data.hasFile))
      setFileName(data.fileName || '')
      setFileUrl(data.fileUrl || '')
      setTotalTime(String(data.timeMinutes ?? 60))
      setCorrectMarks(String(data.correctMarks ?? 1))
      setWrongMarks(String(data.wrongMarks ?? 0))
      const setsCount = Math.max(1, Math.min(4, Number(data.sets ?? 1)))
      setNumberOfSets(setsCount)
      const qCount = Number(data.questions ?? 0)
      setNumberOfQuestions(qCount > 0 ? String(qCount) : '')
      const offset = Number(data.offset ?? 1)
      setQuestionStartsFrom(String(offset))

      // Populate setMatrices from loaded answer keys
      if (qCount > 0) {
        const rawKeys = [
          data.answerKey || '',
          data.answerKey2 || '',
          data.answerKey3 || '',
          data.answerKey4 || '',
        ]
        const matrices = []
        for (let s = 0; s < setsCount; s++) {
          const keyStr = rawKeys[s] || ''
          const rowItems = []
          for (let i = 0; i < qCount; i++) {
            const ansChar = (keyStr[i] || 'A').toUpperCase()
            rowItems.push({
              questionNo: offset + i,
              answer: ANSWER_OPTIONS.includes(ansChar) ? ansChar : 'A',
            })
          }
          matrices.push(rowItems)
        }
        setSetMatrices(matrices)

        if (matrices[0]) {
          setAnswerKeyText(matrices[0].map((r) => `${r.questionNo}:${r.answer}`).join(', '))
        }
      }
    } catch (err) {
      appToast.error(err?.message || 'Failed to load question paper details.')
    } finally {
      setIsLoading(false)
    }
  }, [testId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      appToast.error('Only PDF files can be uploaded as Question Paper.')
      return
    }
    setSelectedFile(file)
    setFileName(file.name)
  }

  const handleDeleteFile = async () => {
    const confirmed = await appConfirm({
      title: 'Remove Question Paper',
      message: 'Are you sure you want to remove this Question Paper PDF file?',
      confirmText: 'Remove',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsSaving(true)
    try {
      await deleteQuestionPaperFile(Number(testId))
      setHasFile(false)
      setFileName('')
      setFileUrl('')
      setSelectedFile(null)
      appToast.success('Question Paper removed successfully.')
    } catch (err) {
      appToast.error(err?.message || 'Failed to remove question paper.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRefreshAnswerKey = () => {
    const qCount = parseInt(numberOfQuestions, 10)
    if (!qCount || qCount <= 0) {
      appToast.warning('Please specify Number of Questions first.')
      return
    }
    const offset = parseInt(questionStartsFrom, 10) || 1
    const setsCount = Number(numberOfSets) || 1

    const newMatrices = []
    for (let s = 0; s < setsCount; s++) {
      const existingSet = setMatrices[s] || []
      const existingMap = new Map(existingSet.map((r) => [r.questionNo, r.answer]))
      const rows = []
      for (let i = 0; i < qCount; i++) {
        const qNo = offset + i
        rows.push({
          questionNo: qNo,
          answer: existingMap.get(qNo) || 'A',
        })
      }
      newMatrices.push(rows)
    }
    setSetMatrices(newMatrices)

    if (newMatrices[0]) {
      setAnswerKeyText(newMatrices[0].map((r) => `${r.questionNo}:${r.answer}`).join(', '))
    }
    appToast.info(`Answer key table refreshed with ${qCount} questions across ${setsCount} set(s).`)
  }

  const handleOptionChange = (setIndex, questionNo, answerVal) => {
    setSetMatrices((prev) => {
      const copy = [...prev]
      if (!copy[setIndex]) return prev
      copy[setIndex] = copy[setIndex].map((r) =>
        r.questionNo === questionNo ? { ...r, answer: answerVal } : r
      )
      return copy
    })
  }

  const handleGetText = () => {
    if (!setMatrices.length || !setMatrices[0]?.length) {
      appToast.warning('Answer key is empty. Click [Refresh] first.')
      return
    }
    const str = setMatrices[0].map((r) => `${r.questionNo}:${r.answer}`).join(', ')
    setAnswerKeyText(str)
    appToast.success('Answer key generated into text preview.')
  }

  const handleSetText = () => {
    if (!answerKeyText.trim()) {
      appToast.warning('Enter text string formatted as "1:A, 2:B, 3:C" to apply.')
      return
    }
    const pairs = answerKeyText.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)
    const map = new Map()
    for (const p of pairs) {
      const [qStr, aStr] = p.split(/[:=\s-]+/)
      const qNum = parseInt(qStr, 10)
      const aVal = (aStr || '').trim().toUpperCase()
      if (qNum && ANSWER_OPTIONS.includes(aVal)) {
        map.set(qNum, aVal)
      }
    }

    if (map.size === 0) {
      appToast.error('Could not parse any valid question:answer pairs.')
      return
    }

    setSetMatrices((prev) => {
      if (!prev.length) return prev
      return prev.map((setRows) =>
        setRows.map((r) => (map.has(r.questionNo) ? { ...r, answer: map.get(r.questionNo) } : r))
      )
    })
    appToast.success(`Applied ${map.size} answer key values.`)
  }

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      if (selectedFile) {
        await uploadQuestionPaperFile(Number(testId), selectedFile)
      }

      const qCount = parseInt(numberOfQuestions, 10) || 0
      const offset = parseInt(questionStartsFrom, 10) || 1

      const answerKeyStrings = [
        setMatrices[0]?.map((r) => r.answer).join('') || '',
        setMatrices[1]?.map((r) => r.answer).join('') || '',
        setMatrices[2]?.map((r) => r.answer).join('') || '',
        setMatrices[3]?.map((r) => r.answer).join('') || '',
      ]

      await saveQuestionPaper(Number(testId), {
        timeMinutes: parseInt(totalTime, 10) || 60,
        correctMarks: parseFloat(correctMarks) || 1,
        wrongMarks: parseFloat(wrongMarks) || 0,
        sets: Number(numberOfSets) || 1,
        questions: qCount,
        offset: offset,
        answerKey: answerKeyStrings[0],
        answerKey2: answerKeyStrings[1],
        answerKey3: answerKeyStrings[2],
        answerKey4: answerKeyStrings[3],
      })

      appToast.success('Question Paper settings saved successfully.')
      navigate('/education/tests')
    } catch (err) {
      appToast.error(err?.message || 'Failed to save question paper.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── Page Header (Rule 87: Canopy Hierarchy - Back button to parent tests list) ── */}
      <section className="flex flex-col gap-4 border-b border-outline-variant/10 pb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/education/tests')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
            title="Back to Tests"
          >
            <span className="material-symbols-outlined text-xl">arrow_back</span>
          </button>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
              Question Paper
            </h1>
            <p className="mt-1 text-xs text-on-surface-variant">
              Upload examination paper PDF and configure multi-set answer keys for evaluation.
            </p>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center text-sm font-medium text-on-surface-variant">
          Loading question paper details...
        </div>
      ) : (
        /* ── Main Content Grid (Matching Image 1) ── */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ── Left Column: Form & Key Matrix ── */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm flex flex-col gap-5">
              {/* Existing file notification banner (Matching Image 1) */}
              <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-low/60 p-4">
                {hasFile ? (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-xs text-on-surface">
                      This test already has a Question Paper loaded.{' '}
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold text-primary underline hover:text-primary/80"
                        >
                          Click here to view
                        </a>
                      ) : (
                        <span className="font-semibold text-primary">{fileName}</span>
                      )}
                      {' or select another file to replace'}
                    </p>
                    <div>
                      <button
                        type="button"
                        onClick={handleDeleteFile}
                        className="text-xs font-bold text-error underline hover:text-error/80 cursor-pointer"
                      >
                        Click here to Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-on-surface font-medium">
                    Upload a Question Paper PDF for this test:
                  </p>
                )}
              </div>

              {/* File selector row */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={selectedFile ? selectedFile.name : fileName || ''}
                  placeholder="No file chosen"
                  className="flex-1 rounded-xl border border-outline-variant/30 bg-surface px-4 py-2 text-xs text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none"
                />
                <label className="inline-flex items-center justify-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-high px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-highest cursor-pointer transition-colors shrink-0">
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  Select File
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
              </div>

              {/* Parameters form (Matching Image 1) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Total time (in minutes)
                  </label>
                  <input
                    type="number"
                    value={totalTime}
                    onChange={(e) => setTotalTime(e.target.value)}
                    min="1"
                    placeholder="60"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Marks to be given for correct answers
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    value={correctMarks}
                    onChange={(e) => setCorrectMarks(e.target.value)}
                    placeholder="1"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Marks to be deducted for Wrong answers
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    value={wrongMarks}
                    onChange={(e) => setWrongMarks(e.target.value)}
                    placeholder="0"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Number of Sets
                  </label>
                  <select
                    value={numberOfSets}
                    onChange={(e) => setNumberOfSets(Number(e.target.value))}
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  >
                    {SET_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Number of Questions
                  </label>
                  <input
                    type="number"
                    value={numberOfQuestions}
                    onChange={(e) => setNumberOfQuestions(e.target.value)}
                    min="1"
                    placeholder="e.g. 50"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-on-surface block mb-1">
                    Question No. starts from
                  </label>
                  <input
                    type="number"
                    value={questionStartsFrom}
                    onChange={(e) => setQuestionStartsFrom(e.target.value)}
                    min="1"
                    placeholder="1"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Answer Key Refresh action (Matching Image 1) */}
              <div className="flex items-center gap-3 pt-2 border-t border-outline-variant/15">
                <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
                  Answer Key
                </span>
                <button
                  type="button"
                  onClick={handleRefreshAnswerKey}
                  className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                >
                  Refresh
                </button>
              </div>

              {/* Text representation & Get/Set buttons (Matching Image 1) */}
              <div className="flex items-start gap-3">
                <textarea
                  rows={3}
                  value={answerKeyText}
                  onChange={(e) => setAnswerKeyText(e.target.value)}
                  placeholder="String representation: 1:A, 2:B, 3:C, 4:D..."
                  className="flex-1 rounded-xl border border-outline-variant/30 bg-surface p-3 font-mono text-xs text-on-surface focus:outline-none resize-vertical"
                />
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleGetText}
                    className="rounded-xl border border-outline-variant/30 bg-surface-container-high px-3 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-highest cursor-pointer"
                  >
                    Get
                  </button>
                  <button
                    type="button"
                    onClick={handleSetText}
                    className="rounded-xl border border-outline-variant/30 bg-surface-container-high px-3 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-highest cursor-pointer"
                  >
                    Set
                  </button>
                </div>
              </div>

              {/* Matrix of Answer options across Sets */}
              {setMatrices.length > 0 && (
                <div className="space-y-6 pt-4 border-t border-outline-variant/15">
                  {setMatrices.map((setRows, setIdx) => (
                    <div key={setIdx} className="space-y-3">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                        Set {setIdx + 1} Answer Key ({setRows.length} questions)
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {setRows.map((r) => (
                          <div
                            key={r.questionNo}
                            className="flex items-center justify-between rounded-xl border border-outline-variant/20 bg-surface-container-low/40 px-3 py-1.5 text-xs"
                          >
                            <span className="font-bold text-on-surface w-8">
                              Q{r.questionNo}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {ANSWER_OPTIONS.map((opt) => (
                                <label
                                  key={opt}
                                  className={`flex h-5 w-5 items-center justify-center rounded cursor-pointer text-[10px] font-bold transition-all ${
                                    r.answer === opt
                                      ? opt === 'X'
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-primary text-on-primary'
                                      : 'bg-surface text-on-surface-variant hover:bg-surface-container-high'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`set_${setIdx}_q_${r.questionNo}`}
                                    value={opt}
                                    checked={r.answer === opt}
                                    onChange={() => handleOptionChange(setIdx, r.questionNo, opt)}
                                    className="sr-only"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Right Column: Tips Box (Matching Image 1) ── */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-3xl border border-outline-variant/20 bg-slate-50/80 p-6 sticky top-6 shadow-sm">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-base text-primary">lightbulb</span>
                Tip
              </h4>
              <ul className="space-y-3 text-xs text-on-surface-variant leading-relaxed">
                {TIPS.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}

      {/* ── Standard Save Bar (Matching Image 1 Submit / Cancel) ── */}
      <AppFormFooterBar
        cancelPath="/education/tests"
        onSaveExit={handleSubmit}
        saveLabel="Submit"
        isSaving={isSaving}
      />
    </div>
  )
}
